import {HttpClient, HttpParams} from '@angular/common/http';
import {Component} from '@angular/core';
import * as moment from 'moment-timezone';
import {WebHttpUrlEncodingCodec} from '@app/app/shared/helpers/web.http.url.encoding.codec';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {ParksFetcher} from '@app/app/shared/helpers/parks.fetcher';

@Component({
  selector: 'app-reports',
  styleUrls: ['reports.css'],
  templateUrl: './reports.component.html'
})
export class ReportsComponent {
  public filters = [
    {
      label: 'Water consumption',
      id: 'water_consumption',
      aggregations: ['sum', 'min', 'max', 'avg', 'first_quartile', 'median', 'third_quartile'],
      defaultAggregation: 'avg',
    },
    {
      label: 'Water temperature',
      id: 'water_temperature',
      aggregations: ['min', 'max', 'avg', 'first_quartile', 'median', 'third_quartile'],
      defaultAggregation: 'avg',
    },
    {
      label: 'Filter state(sand)',
      id: 'filter_state_sand',
      aggregations: [],
      defaultAggregation: '',
    },
    {
      label: 'Filter state(Y)',
      id: 'filter_state_y',
      aggregations: [],
      defaultAggregation: '',
    },
    {
      label: 'Controller temperature',
      id: 'controller_temperature',
      aggregations: ['min', 'max', 'avg', 'first_quartile', 'median', 'third_quartile'],
      defaultAggregation: 'avg',
    },
    {
      label: 'Electrical consumption',
      id: 'electrical_consumption',
      aggregations: ['sum', 'min', 'max', 'avg', 'first_quartile', 'median', 'third_quartile'],
      defaultAggregation: 'avg',
    },
    {
      label: 'Air temperature',
      id: 'air_temperature',
      aggregations: ['min', 'max', 'avg', 'first_quartile', 'median', 'third_quartile'],
      defaultAggregation: 'avg',
    },
    {
      label: 'Delta level chemistry acid',
      id: 'delta_acid',
      aggregations: [],
      defaultAggregation: '',
    },
    {
      label: 'Delta level chemistry chlorine',
      id: 'delta_chlorine',
      aggregations: [],
      defaultAggregation: '',
    },
    {
      label: 'pH value',
      id: 'ph',
      aggregations: ['min', 'max', 'avg', 'first_quartile', 'median', 'third_quartile'],
      defaultAggregation: 'avg',
    },
    {
      label: 'ORP value',
      id: 'orp',
      aggregations: ['min', 'max', 'avg', 'first_quartile', 'median', 'third_quartile'],
      defaultAggregation: 'avg',
    },
    {
      label: 'Daily usage',
      id: 'daily_usage',
      aggregations: ['min', 'max', 'avg', 'first_quartile', 'median', 'third_quartile'],
      defaultAggregation: 'avg',
    },
  ];

  public aggregations = [];
  public parks = [];

  public chartOptions = [];

  public availableAggregations = [
    {id: 'sum', label: 'Sum'},
    {id: 'min', label: 'Minimum'},
    {id: 'max', label: 'Maximum'},
    {id: 'avg', label: 'Average'},
    {id: 'first_quartile', label: 'First Quartile'},
    {id: 'median', label: 'Median'},
    {id: 'third_quartile', label: 'Third Quartile'},
    {id: 'count', label: 'Count'},
  ];

  public selectedFilter = '';
  public selectedAggregation = '';
  public selectedFromDate = null;
  public selectedToDate = null;
  public selectedParks = [];

  public minDate = new Date(1960, 0, 1);
  public maxDate = new Date(2100, 0, 1);

  public isLoadingResults = false;

  constructor(private http: HttpClient, private parksFetcher: ParksFetcher) {
    this.selectedFilter = 'water_consumption';
    this.selectedAggregation = 'average';
    this.selectedFromDate = moment().startOf('year').toDate();
    this.selectedToDate = moment().endOf('day').toDate();

    this.reloadAggregations();

    try {
      const parks = JSON.parse(sessionStorage.getItem('parks'));

      if (Array.isArray(parks) && parks.length > 0 && parks.length < 3) {
        this.selectedParks = parks;
      }
    } catch (e) {
      console.log(e);
    }

    this.isLoadingResults = true;

    this.parksFetcher.retrieveParks().subscribe((res) => {
      this.parks = res;

      if (Array.isArray(this.parks) && this.parks.length > 0) {
        if (this.selectedParks.length < 1) {
          this.selectedParks.push(this.parks[0].id);
        }

        this.reloadData();
      }
    }, (err) => {
      this.isLoadingResults = false;
    });
  }

  public isAvailable(n) {
    if (this.selectedFilter === 'filter_state_sand' || this.selectedFilter === 'filter_state_y') {
      return (n === 4);
    }

    return true;
  }

  private reloadAggregations() {
    this.aggregations = [];
    let aggregations = [];

    this.filters.forEach(filter => {
      if (filter.id === this.selectedFilter) {
        aggregations = filter.aggregations;
        this.selectedAggregation = filter.defaultAggregation;
      }
    });

    aggregations.forEach(aggregation => {
      this.availableAggregations.forEach(availableAggregation => {
        if (aggregation === availableAggregation.id) {
          this.aggregations.push(availableAggregation);
        }
      });
    });
  }

  setStartDate(event) {
    this.selectedFromDate = moment(event.value).startOf('day').toDate();
    this.reloadData();
  }

  setEndDate(event) {
    this.selectedToDate = moment(event.value).endOf('day').toDate();
    this.reloadData();
  }

  toggleType(value) {
    this.selectedFilter = value;
    this.reloadAggregations();
    this.reloadData();
  }

  toggleAggregation(value) {
    this.selectedAggregation = value;
    this.reloadData();
  }

  setParks(value) {
    this.selectedParks = value;

    sessionStorage.setItem('parks', JSON.stringify(this.selectedParks));

    this.reloadData();
  }

  private prepareDate(date) {
    const formattedDate = moment(date).format('YYYY-MM-DD HH:mm:ss.SSS');
    const inUtc = moment.tz(formattedDate, 'YYYY-MM-DD HH:mm:ss.SSS', 'UTC');

    return inUtc.valueOf();
  }

  private prepareParams1(selectedPark) {
    return new HttpParams({encoder: new WebHttpUrlEncodingCodec()})
      .set('type', this.selectedFilter)
      .set('interval', 'day')
      .set('from', this.prepareDate(this.selectedFromDate))
      .set('to', this.prepareDate(this.selectedToDate))
      .set('aggregation', this.selectedAggregation)
      .set('park', selectedPark);
  }

  private prepareParams2(selectedPark) {
    return new HttpParams({encoder: new WebHttpUrlEncodingCodec()})
      .set('type', this.selectedFilter)
      .set('interval', 'week')
      .set('from', this.prepareDate(this.selectedFromDate))
      .set('to', this.prepareDate(this.selectedToDate))
      .set('aggregation', this.selectedAggregation)
      .set('park', selectedPark);
  }

  private prepareParams3(selectedPark) {
    return new HttpParams({encoder: new WebHttpUrlEncodingCodec()})
      .set('type', this.selectedFilter)
      .set('interval', 'year')
      .set('from', this.prepareDate(this.selectedFromDate))
      .set('to', this.prepareDate(this.selectedToDate))
      .set('aggregation', this.selectedAggregation)
      .set('park', selectedPark);
  }

  private prepareParams4() {
    let params = new HttpParams({encoder: new WebHttpUrlEncodingCodec()})
      .set('type', this.selectedFilter)
      .set('from', this.prepareDate(this.selectedFromDate))
      .set('to', this.prepareDate(this.selectedToDate))
      .set('interval', 'over_time');

    this.selectedParks.forEach(selectedPark => {
      params = params.append('parks[]', selectedPark);
    });

    return params;
  }

  private getData(n, selectedPark) {
    if (n === 1) {
      return this.http.get<any>('/reports/histogram', {params: this.prepareParams1(selectedPark)});
    } else if (n === 2) {
      return this.http.get<any>('/reports/histogram', {params: this.prepareParams2(selectedPark)});
    } else if (n === 3) {
      return this.http.get<any>('/reports/histogram', {params: this.prepareParams3(selectedPark)});
    } else if (n === 4) {
      return this.http.get<any>('/reports/custom', {params: this.prepareParams4()});
    }
  }

  private prepareParkName(selectedPark) {
    let pp = '';

    this.parks.forEach((park) => {
      if (park.id === selectedPark) {
        pp = park.attributes.name;
      }
    });

    return pp;
  }

  private chunk(arr, n) {
    let i;
    let j;
    const chunks = [];

    for (i = 0, j = arr.length; i < j; i += n) {
      chunks.push(arr.slice(i, i + n));
    }

    return chunks;
  }

  private xAxisLabel(index) {
    const agg = this.availableAggregations.filter(aggregation => aggregation.id === this.selectedAggregation);

    let a = 'AVERAGE';

    if (agg.length === 1) {
      a = agg[0].label.toUpperCase();
    }

    const captions = [
      `Hours of Day (${a})`,
      `Days of Week (${a})`,
      `Months of Year (${a})`,
      'CONTINUOUS data over selected time range',
    ];

    return captions[index];
  }

  private prepareLabels(labels, index) {
    if (index === 0) {
      return labels.map(l => moment(l, 'H').format('HH:mm'));
    } else if (index === 1) {
      return labels.map(l => {
        if (parseInt(l, 10) === 7) {
          l = 0;
        }

        return moment(l, 'd').format('ddd');
      });
    } else if (index === 2) {
      return labels.map(l => moment(l, 'M').format('MMM'));
    }

    return [];
  }

  private processResult(result, index) {
    if (!this.isAvailable(1)) {
      index = 3;
    }

    const series = [];
    const legends = [];
    let dataLabels = [];
    let pN = 0;

    const caption = this.filters.filter(filter => filter.id === this.selectedFilter)[0].label;

    if (index === 3) {
      dataLabels = result[0].labels.map(val => moment(val).format('MMM D HH:mm'));

      Object.keys(result[0].values).forEach(id => {
        const cap = caption + ' for ' + this.prepareParkName(id);
        legends.push(cap);

        const values = result[0].values[id];

        series.push({
          name: cap,
          type: (index !== 3) ? 'bar' : 'line',
          label: {
            show: (index !== 3),
            rotate: 90,
            position: 'insideBottom',
            distance: 10,
            align: 'left',
            verticalAlign: 'middle',
            color: '#363636',
          },
          data: values
        });
      });
    } else {
      result.forEach(res => {
        dataLabels = this.prepareLabels(res.labels, index);

        const cap = caption + ' for ' + this.prepareParkName(this.selectedParks[pN++]);
        legends.push(cap);

        series.push({
          name: cap,
          type: (index !== 3) ? 'bar' : 'line',
          label: {
            show: (index !== 3),
            rotate: 90,
            position: 'insideBottom',
            distance: 10,
            align: 'left',
            verticalAlign: 'middle',
            color: '#363636',
          },
          data: res.values.map(val => val.toFixed(2))
        });
      });
    }

    this.chartOptions[index] = {
      color: ['#4fc3f7', '#f74f8a'],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        top: 20,
        data: legends,
      },
      grid: {
        top: 80,
        left: 80,
        right: (index === 3) ? 50 : 20,
        bottom: (index !== 3) ? 80 : 100,
        containLabel: false
      },
      dataZoom: [
        {
          type: 'slider',
          show: (index === 3),
          start: 0,
          end: 100,
          left: 100,
          right: 100,
        }
      ],
      xAxis: [
        {
          type: 'category',
          data: dataLabels,
          axisLabel: {
            rotate: (index === 3) ? 0 : 90,
            interval: 'auto',
          },
          name: this.xAxisLabel(index),
          nameLocation: 'middle',
          nameGap: (index !== 3) ? 60 : 40,
        }
      ],
      yAxis: [
        {
          show: true,
          type: 'value',
          name: this.getYAxisLabel(),
          nameLocation: 'middle',
          nameGap: 55,
          axisLabel: {
            show: true,
            rotate: 0,
            formatter: (value) => {
              if (value >= 1000000000) {
                return (value / 1000000000).toFixed(1).replace(/\.0$/, '') + 'G';
              }
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
              }
              if (value >= 1000) {
                return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
              }
              return value;
            }
          }
        }
      ],
      series: series,
    };
  }

  private getYAxisLabel() {
    switch (this.selectedFilter) {
      case 'water_consumption':
        return 'Liters';
      case 'water_temperature':
      case 'air_temperature':
      case 'controller_temperature':
        return 'Degree Celsius ℃';
      case 'filter_state_sand':
      case 'filter_state_y':
      case 'daily_usage':
      case 'delta_acid':
      case 'delta_chlorine':
        return 'Percentage';
      case 'orp':
        return 'Millivolts mV';
      case 'ph':
        return 'ph';
      case 'electrical_consumption':
        return 'kWh';
    }

    return '';
  }

  private reloadData() {
    this.isLoadingResults = true;

    const requests = [];

    if (this.isAvailable(1)) {
      [1, 2, 3].forEach((attribute) => {
        this.selectedParks.forEach(selectedPark => {
          requests.push(this.getData(attribute, selectedPark));
        });
      });
    }

    requests.push(this.getData(4, this.selectedParks));

    const selParks = this.selectedParks.length;

    forkJoin(requests).subscribe((results) => {
      this.isLoadingResults = false;

      this.chunk(results, selParks).forEach((chunk, index) => {
        this.processResult(chunk, index);
      });
    }, (err) => {
      this.isLoadingResults = false;
    }, () => {
      this.isLoadingResults = false;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/reports/reports.component.ts